import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Plus, Edit, Trash2, Eye, Calendar, MapPin, 
  ToggleLeft, ToggleRight, Search, ExternalLink,
  Copy, MoreVertical, Ticket
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || '';

const AdminLandingPages = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/admin/event-landing-pages`);
      setPages(res.data);
    } catch (err) {
      console.error("Error fetching landing pages:", err);
      toast.error("Failed to load landing pages");
    } finally {
      setLoading(false);
    }
  };

  const togglePageStatus = async (pageId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/event-landing-pages/${pageId}`, {
        is_active: !currentStatus
      });
      setPages(pages.map(p => 
        p.id === pageId ? { ...p, is_active: !currentStatus } : p
      ));
      toast.success(`Page ${!currentStatus ? 'activated' : 'deactivated'}`);
    } catch (err) {
      toast.error("Failed to update page status");
    }
  };

  const deletePage = async (pageId) => {
    try {
      await axios.delete(`${API}/admin/event-landing-pages/${pageId}`);
      setPages(pages.filter(p => p.id !== pageId));
      toast.success("Landing page deleted");
      setDeleteConfirm(null);
    } catch (err) {
      toast.error("Failed to delete page");
    }
  };

  const duplicatePage = async (page) => {
    try {
      const newPage = {
        ...page,
        slug: `${page.slug}-copy`,
        title: `${page.title} (Copy)`,
        is_active: false
      };
      delete newPage.id;
      delete newPage.created_at;
      delete newPage.updated_at;
      
      const res = await axios.post(`${API}/admin/event-landing-pages`, newPage);
      setPages([...pages, res.data]);
      toast.success("Page duplicated");
    } catch (err) {
      toast.error("Failed to duplicate page");
    }
  };

  const copyUrl = (slug) => {
    const url = `${window.location.origin}/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("URL copied to clipboard");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "No date";
    // Accept "YYYY-MM-DD" or pass-through any non-ISO label
    const isoMatch = /^\d{4}-\d{2}-\d{2}/.test(dateStr);
    if (!isoMatch) return dateStr; // e.g. "August 13–15, 2026"
    const [year, month, day] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const filteredPages = pages.filter(page =>
    page.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.slug?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#F5F5F0]">Event Landing Pages</h1>
            <p className="text-[#A1A1AA] mt-1">Manage standalone event pages with Stripe checkout</p>
          </div>
          <Link
            to="/admin/landing-pages/new"
            className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8943B] transition-colors"
            data-testid="create-landing-page-btn"
          >
            <Plus className="w-5 h-5" />
            Create Landing Page
          </Link>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a1a1aa]" />
          <input
            type="text"
            placeholder="Search pages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-[#121212] border border-[#27272A] text-[#F5F5F0] placeholder-[#52525B] focus:outline-none focus:border-[#D4AF37]"
            data-testid="search-landing-pages"
          />
        </div>

        {/* Pages Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#D4AF37] border-t-transparent"></div>
          </div>
        ) : filteredPages.length === 0 ? (
          <div className="text-center py-20 bg-[#121212] border border-[#27272A]">
            <Ticket className="w-12 h-12 text-[#a1a1aa] mx-auto mb-4" />
            <p className="text-[#A1A1AA] mb-4">
              {searchTerm ? "No pages match your search" : "No landing pages yet"}
            </p>
            {!searchTerm && (
              <Link
                to="/admin/landing-pages/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8943B] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Your First Page
              </Link>
            )}
          </div>
        ) : (
          <div className="grid gap-6">
            {filteredPages.map((page, index) => (
              <motion.div
                key={page.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-[#121212] border ${page.is_active ? 'border-[#27272A]' : 'border-[#52525B]/50'} p-6 hover:border-[#D4AF37]/50 transition-colors`}
                data-testid={`landing-page-${page.slug}`}
              >
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Page Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-[#F5F5F0]">{page.title}</h3>
                          <span className={`px-2 py-0.5 text-xs font-medium ${
                            page.is_active 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-[#52525B]/20 text-[#a1a1aa]'
                          }`}>
                            {page.is_active ? 'Active' : 'Draft'}
                          </span>
                        </div>
                        <p className="text-[#A1A1AA] text-sm mb-3">{page.subtitle}</p>
                        
                        <div className="flex flex-wrap gap-4 text-sm">
                          <div className="flex items-center gap-1.5 text-[#A1A1AA]">
                            <Calendar className="w-4 h-4 text-[#D4AF37]" />
                            {page.date_label || page.event_date || formatDate(page.date)}
                          </div>
                          <div className="flex items-center gap-1.5 text-[#A1A1AA]">
                            <MapPin className="w-4 h-4 text-[#D4AF37]" />
                            {page.location || page.venue || page.address_line || "No address"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* URL */}
                    <div className="mt-4 flex items-center gap-2">
                      <code className="text-sm text-[#D4AF37] bg-[#0F0F0F] px-3 py-1.5 border border-[#27272A]">
                        /{page.slug}
                      </code>
                      <button
                        onClick={() => copyUrl(page.slug)}
                        className="p-1.5 text-[#a1a1aa] hover:text-[#D4AF37] transition-colors"
                        title="Copy URL"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <a
                        href={`/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-[#a1a1aa] hover:text-[#D4AF37] transition-colors"
                        title="Open page"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>

                    {/* Tickets Preview */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {page.ticket_options?.slice(0, 3).map((ticket, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-[#0F0F0F] border border-[#27272A] text-[#A1A1AA]">
                          {ticket.name}: ${ticket.price}
                        </span>
                      ))}
                      {page.ticket_options?.length > 3 && (
                        <span className="text-xs px-2 py-1 text-[#a1a1aa]">
                          +{page.ticket_options.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex lg:flex-col gap-2 lg:w-40">
                    {page.is_virtual_market_camp ? (
                      <span
                        className="flex-1 lg:w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium bg-green-500/10 text-green-400"
                        data-testid={`badge-${page.slug}`}
                      >
                        <ToggleRight className="w-4 h-4" />
                        Live (CMS-managed)
                      </span>
                    ) : (
                      <button
                        onClick={() => togglePageStatus(page.id, page.is_active)}
                        className={`flex-1 lg:w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                          page.is_active
                            ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                            : 'bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46]'
                        }`}
                        data-testid={`toggle-${page.slug}`}
                      >
                        {page.is_active ? (
                          <>
                            <ToggleRight className="w-4 h-4" />
                            Active
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="w-4 h-4" />
                            Draft
                          </>
                        )}
                      </button>
                    )}

                    <Link
                      to={page.is_virtual_market_camp ? (page.edit_url || "/admin/market-camp-settings") : `/admin/landing-pages/${page.id}`}
                      className="flex-1 lg:w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium bg-[#D4AF37] text-[#050505] hover:bg-[#B8943B] transition-colors"
                      data-testid={`edit-${page.slug}`}
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </Link>

                    {!page.is_virtual_market_camp && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => duplicatePage(page)}
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-[#A1A1AA] bg-[#27272A] hover:bg-[#3F3F46] transition-colors"
                          title="Duplicate"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(page.id)}
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors"
                          title="Delete"
                          data-testid={`delete-${page.slug}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
            <div className="bg-[#121212] border border-[#27272A] p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-[#F5F5F0] mb-2">Delete Landing Page?</h3>
              <p className="text-[#A1A1AA] mb-6">
                This will permanently delete this landing page. This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 bg-[#27272A] text-[#F5F5F0] hover:bg-[#3F3F46] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deletePage(deleteConfirm)}
                  className="flex-1 px-4 py-2 bg-red-500 text-white hover:bg-red-600 transition-colors"
                  data-testid="confirm-delete"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminLandingPages;
